import "./transactions.css";
import {
  profilePurpleIcons,
  profileFilledDarkIcons,
} from "../../utils/globalImages";
import { Eye, X } from "lucide-react";
import {
  formatDateTime,
  shortenTextWithReadMore,
} from "../../utils/HelperFunctions";
import { Search } from "lucide-react";
import { useSelector } from "react-redux";
import { useRef, useEffect, useState } from "react";
import { Withdrawal } from "../../api/modules/b2droppie.class";
import Pagination from "../../components/Pagination/Pagination";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";

const Withdrawals = () => {
  const sectionTopRef = useRef(null);
  const isSortingRef = useRef(false);
  const [search, setSearch] = useState("");
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [withdrawals, setWithdrawals] = useState([]);
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Drag functionality states
  const [dragPosition, setDragPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const modalRef = useRef(null);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchWithdrawals(currentPage, search, sortBy, sortOrder);
    }, 400);

    return () => clearTimeout(delay);
  }, [currentPage, search, sortBy, sortOrder]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  const fetchWithdrawals = async (
    page = 1,
    searchValue = search,
    sortField = sortOrder,
    sortDirection = sortBy,
  ) => {
    if (!isSortingRef.current) {
      setLoading(true);
    }
    try {
      const res = await Withdrawal(page, searchValue, sortField, sortDirection);
      setWithdrawals(res?.data?.data || []);
      setCurrentPage(res?.data?.current_page || 1);
      setLastPage(res?.data?.last_page || 1);
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
    } finally {
      setLoading(false);
      isSortingRef.current = false;
    }
  };

  const handleSort = (field) => {
    isSortingRef.current = true;
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "⇅";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const statusBadge = (status) => {
    const normalized = status?.toString()?.trim()?.toUpperCase();

    return (
      <span className={`custom-status-badge ${normalized?.toLowerCase()}`}>
        {normalized}
      </span>
    );
  };

  const numericStatusBadge = (status) => {
    let statusText = "";
    let statusClass = "";

    switch (Number(status)) {
      case 0:
        statusText = "INACTIVE";
        statusClass = "inactive";
        break;
      case 1:
        statusText = "ACTIVE";
        statusClass = "active";
        break;
      default:
        statusText = "UNKNOWN";
        statusClass = "unknown";
        break;
    }

    return (
      <span className={`custom-status-badge ${statusClass}`}>{statusText}</span>
    );
  };

  const handleViewItems = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowModal(true);
    setDragPosition(0);
  };

  // Drag handlers for mobile
  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0) {
      setDragPosition(diff);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragPosition > 100) {
      setShowModal(false);
      setTimeout(() => setDragPosition(0), 300);
    } else {
      setDragPosition(0);
    }
  };

  return (
    <>
      <div
        ref={sectionTopRef}
        className={`transactions-header d-space-between ${isDarkMode ? "dark" : "light"}`}
      >
        <div className="d-space-between w-100">
          <div
            className={`d-start gap-16px ${searchOpen ? "hide-on-mobile-screen" : ""}`}
          >
            <div className="transactions-icon">
              <img
                alt="Withdrawals Icon"
                src={
                  isDarkMode
                    ? profileFilledDarkIcons.withdrawalsFilledDark
                    : profilePurpleIcons.withdrawalsFilledPurple
                }
              />
            </div>
            <div className="transactions-header-text">
              <h1
                className={`transactions-title-header ${isDarkMode ? "text-white" : "text-deep-purple"}`}
              >
                Withdrawals
              </h1>
              <p className="transactions-subtitle-header text-light-gray">
                View and manage your withdrawal history
              </p>
            </div>
          </div>
          <div className={`search-wrapper ${searchOpen ? "open" : ""}`}>
            <div
              className="search-toggle"
              onClick={() => {
                setSearchOpen(!searchOpen);
                if (!searchOpen) {
                  setTimeout(() => {
                    document.querySelector(".search-input")?.focus();
                  }, 300);
                }
              }}
            >
              {searchOpen ? <X size={18} /> : <Search size={18} />}
            </div>
            <input
              type="text"
              value={search}
              placeholder="Search"
              className="search-input"
              onChange={(e) => {
                setCurrentPage(1);
                setSearch(e.target.value);
              }}
              onBlur={() => {
                if (!search) {
                  setTimeout(() => setSearchOpen(false), 200);
                }
              }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <BackdropLoader show={loading} />
      ) : (
        <div
          className={`myaccount-table-container ${
            isDarkMode ? "dark" : "light"
          }`}
        >
          <div className="myaccount-table-wrapper">
            <table className="profile-tables">
              <thead>
                <tr>
                  <th className="ps-4">Transaction ID</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort("payment_amount")}
                  >
                    Amount {getSortIcon("payment_amount")}
                  </th>
                  <th onClick={() => handleSort("status")} className="sortable">
                    Status {getSortIcon("status")}
                  </th>

                  <th
                    className="sortable"
                    onClick={() => handleSort("created_at")}
                  >
                    Created at {getSortIcon("created_at")}
                  </th>

                  <th>Items</th>
                </tr>
              </thead>

              <tbody>
                {withdrawals.length > 0 ? (
                  withdrawals.map((item, index) => (
                    <tr key={index} className="withdrawal-row">
                      <td className="wallet-address ps-4">
                        {item?.uuid || "N/A"}
                      </td>
                      <td className="amount">
                        ${item?.payment_amount ?? "0.00"}
                      </td>

                      <td>{statusBadge(item?.status)}</td>
                      <td>{formatDateTime(item?.created_at)}</td>
                      <td>
                        <button
                          className="eye-button"
                          onClick={() => handleViewItems(item)}
                        >
                          <Eye size={20} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">
                      No withdrawals found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <Pagination
              lastPage={lastPage}
              currentPage={currentPage}
              scrollTargetRef={sectionTopRef}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      )}

      {/* Modal for viewing withdrawal items */}
      {showModal && selectedWithdrawal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.className === "modal-overlay") {
              setShowModal(false);
            }
          }}
        >
          <div
            ref={modalRef}
            className={`modal-container ${isDarkMode ? "modal-dark" : "modal-light"}`}
            style={{
              transform: `translateY(${dragPosition}px)`,
              transition: isDragging ? "none" : "transform 0.3s ease",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Drag Handle - Mobile Only */}
            <div className="modal-drag-handle">
              <div className="drag-bar"></div>
            </div>

            {/* Modal Header */}
            <div className="modal-header-custom">
              <h5 className="modal-title-custom">
                {selectedWithdrawal?.type || "Withdrawal Products"}
              </h5>
              <button
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body-custom">
              {selectedWithdrawal?.products?.length > 0 ? (
                <>
                  {/* Desktop View */}
                  <div className="desktop-table-view">
                    <table
                      className={`products-table ${
                        isDarkMode ? "products-table-dark" : ""
                      }`}
                    >
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Description</th>
                          <th>Color</th>
                          <th>Status</th>
                          <th>Quantity</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedWithdrawal.products.map((product, i) => (
                          <tr key={i}>
                            <td>
                              <div className="product-cell">
                                <img
                                  alt={product.name}
                                  src={product.image}
                                  className="product-image"
                                />
                                <span className="product-name">
                                  {product.name}
                                </span>
                              </div>
                            </td>
                            <td
                              className="description-cell"
                              dangerouslySetInnerHTML={{
                                __html: shortenTextWithReadMore(
                                  product.description,
                                  120,
                                ),
                              }}
                            />
                            <td>
                              <div className="color-cell">
                                <div
                                  className="color-circle"
                                  style={{
                                    backgroundColor: product.product_color,
                                  }}
                                ></div>
                              </div>
                            </td>
                            <td>{numericStatusBadge(product?.status)}</td>
                            <td className="text-center">
                              {product.quantity || 1}
                            </td>
                            <td className="price-cell">${product.price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile View */}
                  <div className="mobile-cards-view">
                    {/* Header */}
                    <div
                      className="mobile-header-row"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.5fr 2fr 1fr",
                        padding: "8px 12px",
                        fontSize: "12px",
                        color: "#9ca3af",
                      }}
                    >
                      <span>Product</span>
                      <span>Description</span>
                      <span style={{ textAlign: "right" }}>Price</span>
                    </div>

                    {selectedWithdrawal.products.map((product, i) => (
                      <div key={i} className="mobile-product-card">
                        {/* Product */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: 0,
                          }}
                        >
                          <img
                            alt={product.name}
                            src={product.image}
                            style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "6px",
                              objectFit: "cover",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {product.name}
                          </span>
                        </div>

                        {/* Description */}
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#9ca3af",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {product.description}
                        </span>

                        {/* Price */}
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            textAlign: "right",
                          }}
                        >
                          ${product.price}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="no-products-text">
                  No products available for this withdrawal.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Withdrawals;
